import type { z } from 'zod'

export class V1RequestError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number | null,
    readonly retryable: boolean,
    readonly problem: Record<string, unknown> | null = null,
    options?: ErrorOptions
  ) {
    super(message, options)
    this.name = 'V1RequestError'
  }
}

const text = (value: unknown) => (typeof value === 'string' ? value : undefined)

export const problemError = (status: number | null, payload: unknown): V1RequestError => {
  const problem =
    typeof payload === 'object' && payload !== null ? (payload as Record<string, unknown>) : null
  const body = problem ?? {}
  return new V1RequestError(
    text(body.code) ?? `HTTP_${status}`,
    text(body.detail) ?? text(body.message) ?? text(body.error) ?? 'The API rejected the operation.',
    status,
    (body.retryable ?? body.isRetryable) === true,
    problem
  )
}

export function createV1Transport(options: {
  baseUrl: string
  headers: HeadersInit
  fetch?: typeof fetch
  /** Aborts every request made through this transport, e.g. a superseded query. */
  signal?: AbortSignal
}) {
  const requestFetch = options.fetch ?? globalThis.fetch
  const baseUrl = new URL(options.baseUrl)
  const abort = options.signal ? { signal: options.signal } : {}

  async function mutate<T>(
    path: string,
    idempotencyKey: string | undefined,
    body: unknown,
    schema: z.ZodType<T>,
    method: 'POST' | 'PATCH' | 'PUT' | 'DELETE' = 'POST',
    policy: { requireIdempotencyKey: boolean } = { requireIdempotencyKey: true }
  ): Promise<T> {
    if (
      (policy.requireIdempotencyKey && !idempotencyKey) ||
      (idempotencyKey !== undefined && !idempotencyKey.trim())
    ) {
      throw new V1RequestError('INVALID_ARGUMENTS', 'A replay key is required.', null, false)
    }
    const headers = new Headers(options.headers)
    headers.set('content-type', 'application/json')
    if (idempotencyKey !== undefined) headers.set('idempotency-key', idempotencyKey)
    let response: Response
    try {
      response = await requestFetch(new URL(path, baseUrl), {
        method,
        headers,
        body: JSON.stringify(body),
        ...abort,
      })
    } catch (error) {
      if (options.signal?.aborted) {
        throw error
      }
      throw new V1RequestError(
        'TRANSPORT_ERROR',
        'The API response was not received; the write outcome is unknown.',
        null,
        false,
        null,
        { cause: error }
      )
    }
    return decodeResponse(response, schema, true)
  }

  async function read<T>(path: string, schema: z.ZodType<T>, body?: unknown): Promise<T> {
    let response: Response
    try {
      const headers = new Headers(options.headers)
      if (body !== undefined) headers.set('content-type', 'application/json')
      response = await requestFetch(new URL(path, baseUrl), {
        method: body === undefined ? 'GET' : 'POST',
        headers,
        ...(body === undefined ? {} : { body: JSON.stringify(body) }),
        ...abort,
      })
    } catch (error) {
      if (options.signal?.aborted) {
        throw error
      }
      throw new V1RequestError(
        'TRANSPORT_ERROR',
        'The API read response was not received.',
        null,
        true,
        null,
        { cause: error }
      )
    }
    return decodeResponse(response, schema, false)
  }

  async function decodeResponse<T>(
    response: Response,
    schema: z.ZodType<T>,
    write: boolean
  ): Promise<T> {
    const payload: unknown = await response.json().catch(() => null)
    if (!response.ok) throw problemError(response.status, payload)
    if (write && response.status === 202) {
      throw new V1RequestError(
        'approval_pending',
        'The action needs approval. Approve the request, then run it again with the same key.',
        202,
        false,
        typeof payload === 'object' && payload !== null
          ? (payload as Record<string, unknown>)
          : null
      )
    }
    const result = schema.safeParse(payload)
    if (!result.success) {
      const fields = result.error.issues
        .slice(0, 3)
        .map((issue) => issue.path.join('.') || 'body')
        .join(', ')
      throw new V1RequestError(
        'INVALID_API_RESPONSE',
        write
          ? `The API accepted the write but returned an invalid receipt (${fields}).`
          : `The API returned an invalid read response (${fields}).`,
        response.status,
        false
      )
    }
    return result.data
  }

  return { read, mutate }
}

export type V1Transport = ReturnType<typeof createV1Transport>
